import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { API_BASE_URL, getAuthConfig } from "../services/api";

function Dashboard(){
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isDeletingUsers, setIsDeletingUsers] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    checkedIn: 0,
    checkedOut: 0,
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [scheduleVisitorId, setScheduleVisitorId] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [issuingPassId, setIssuingPassId] = useState("");

  
  const role = localStorage.getItem("role");
  const userName = localStorage.getItem("userName");
  const currentUserId = localStorage.getItem("userId");
  const canManageVisitors = role === "employee" || role === "admin";
  const canIssuePass = role === "security" || role === "admin";

  const getImageUrl = (filename) =>{
    if (!filename) return "";
    return new URL(`/uploads/photos/${encodeURIComponent(filename)}`, API_BASE_URL).toString();
  };

  const formatDateTime = (value) =>{
    if (!value) return "";

    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCheckStatusClasses = (checkStatus) =>{
    if(checkStatus === "checked-in"){
      return "bg-green-100 text-green-700";
    }

    if(checkStatus === "checked-out"){
      return "bg-orange-100 text-orange-700";
    }
    return "bg-gray-100 text-gray-600";
  };

  const getCheckStatusLabel = (checkStatus) =>{
    if(checkStatus === "checked-in"){
      return "Checked In";
    }

    if(checkStatus === "checked-out"){
      return "Checked Out";
    }
    return "Not Visited";
  };

  const refreshVisitors = async () =>{
    try{
      let url = `/visitors?search=${encodeURIComponent(search)}`;
      if (status !== "all") url += `&status=${status}`;
      const res = await API.get(url, getAuthConfig());
      setVisitors(res.data.visitors);
      setSummary(res.data.summary);
    } 
    catch(err){
      alert("Could not load visitors");
    }
  };

  const refreshUsers = useCallback(async (showError = true) =>{
    if (role !== "admin") return;

    try{
      setUsersLoading(true);
      const res = await API.get("/auth/users", getAuthConfig());
      setUsers(res.data);
    } 
    catch(err){
      if(showError){
        alert(err.response?.data?.message || "Could not load users");
      }
    } 
    finally{
      setUsersLoading(false);
    }
  }, [role]);

  useEffect(() =>{
    const fetchVisitors = async () => {
      try{
        let url = `/visitors?search=${encodeURIComponent(search)}`;
        if(status !== "all") url += `&status=${status}`;
        const res = await API.get(url, getAuthConfig());
        setVisitors(res.data.visitors);
        setSummary(res.data.summary);
      } 
      catch(err){
        alert("Could not load visitors");
      }
    };

    fetchVisitors();
  }, [search, status]);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const approveVisitor = async (id) => {
    try{
      await API.patch(`/visitors/${id}/approve`, {}, getAuthConfig());
      refreshVisitors();
    } 
    catch (err){
      alert(err.response?.data?.message || "Approve failed");
    }
  };

  const rejectVisitor = async (id) =>{
    try{
      await API.patch(`/visitors/${id}/reject`, {}, getAuthConfig());
      refreshVisitors();
    } 
    catch(err){
      alert(err.response?.data?.message || "Reject failed");
    }
  };

  const openScheduleBox = (visitorId) => {
    setScheduleVisitorId(visitorId);
    setVisitDate("");
    setVisitTime("");
  };

  const closeScheduleBox = () => {
    setScheduleVisitorId("");
    setVisitDate("");
    setVisitTime("");
  };

  const scheduleAppointment = async (visitorId) => {
    if(!visitDate || !visitTime){
      alert("Please select date and time");
      return;
    }

    try{
      await API.patch(`/visitors/${visitorId}/schedule`, { visitDate, visitTime }, getAuthConfig());
      closeScheduleBox();
      refreshVisitors();
    } 
    catch(err){
      alert(err.response?.data?.message || "Schedule failed");
    }
  };

  const issuePass = async (id) =>{
    if(issuingPassId === id){
      return;
    }

    try{
      setIssuingPassId(id);
      const res = await API.patch(`/visitors/${id}/issue-pass`, {}, getAuthConfig());
      alert(res.data.message);
      await refreshVisitors();
    } 
    catch(err){
      alert(err.response?.data?.message || "Issue pass failed");
    } 
    finally{
      setIssuingPassId("");
    }
  };

  const handleExport = async () =>{
    try{
      const res = await API.get("/visitors/export", { ...getAuthConfig(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "visitors.csv");
      link.click();
    } 
    catch(err){
      alert("Export failed");
    }
  };

  const deleteUser = async (userId) => {
    const confirmed = window.confirm("Delete this user account?");
    if(!confirmed){
      return;
    }

    try{
      setIsDeletingUsers(true);
      const res = await API.delete(`/auth/users/${userId}`, getAuthConfig());
      alert(res.data.message);
      refreshUsers(false);
    } 
    catch{
      alert("Could not delete user");
    } 
    finally{
      setIsDeletingUsers(false);
    }
  };

  return(
    <div className="min-h-screen bg-gray-100 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex rounded-2xl bg-white p-6 shadow-sm  justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">Welcome, {userName}</p>
          </div>
          <div className="flex gap-4 m-1">
            {canIssuePass && (
              <button onClick={() => navigate("/scan")} className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm transition hover:bg-blue-700">Scan QR</button>
            )}
            <button onClick={() => navigate("/pass-lookup")} className="rounded-lg bg-gray-800 px-4 py-2 text-white shadow-sm transition hover:bg-gray-900">Pass Lookup</button>
            {role === "admin" && (
              <button onClick={handleExport} className="rounded-lg bg-green-600 px-4 py-2 text-white shadow-sm transition hover:bg-green-700">Export CSV</button>
            )}
            <button onClick={() => { localStorage.clear(); navigate("/"); }} className="rounded-lg bg-red-500 px-4 py-2 text-white shadow-sm transition hover:bg-red-600">Logout</button>
          </div>
        </div>

        <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">

          <div className="rounded-2xl bg-white p-4 shadow-sm border">
            <p className="text-sm text-gray-500">Total Visitors</p>
            <p className="mt-2 text-2xl font-semibold text-gray-800">{summary.total}</p>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-sm border">
            <p className="text-sm text-gray-500">Pending Approvals</p>
            <p className="mt-2 text-2xl font-semibold text-yellow-600">{summary.pending}</p>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-sm border">
            <p className="text-sm text-gray-500">Checked In</p>
            <p className="mt-2 text-2xl font-semibold text-green-600">{summary.checkedIn}</p>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-sm border">
            <p className="text-sm text-gray-500">Checked Out</p>
            <p className="mt-2 text-2xl font-semibold text-orange-600">{summary.checkedOut}</p>
          </div>

        </div>

        <div className="mb-6 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm border md:flex-row">
          <input
            className="flex-1 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm outline-none transition focus:border-blue-400 "
            placeholder="Search visitors..."
            onChange={e => setSearch(e.target.value)}/>
            
          <select className="rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none transition focus:border-blue-400" onChange={e => setStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {canManageVisitors && (
            <button onClick={() => navigate("/add")} className="rounded-xl bg-blue-600 px-6 py-2 text-white shadow-sm transition hover:bg-blue-700">Add Visitor</button>
          )}
        </div>

        <div className="grid gap-5">
          {visitors.length === 0 && (
            <div className="rounded-2xl bg-white p-8 text-center text-gray-500 shadow-sm border">
              No visitors found for the current filters.
            </div>
          )}

          {visitors.map((visitor) => (
            <div key={visitor._id} className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm border transition hover:ring-blue-200 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 overflow-hidden rounded-full border border-gray-200 bg-gray-50">
                  {visitor.photo ? (
                    <img
                      src={getImageUrl(visitor.photo)}
                      alt={visitor.name}
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-center text-sm text-gray-400">No Photo</div>
                  )}
                </div>

                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-800">{visitor.name}</h3>
                  <p className="text-sm text-gray-500">{visitor.email}</p>
                  <p className="text-sm text-gray-500">{visitor.purpose || "General Visit"}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${visitor.status === "approved" ? "bg-green-100 text-green-700" : visitor.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {visitor.status}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${visitor.isScheduled ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                      {visitor.isScheduled ? "scheduled" : "not scheduled"}
                    </span>
                    {visitor.passIssued && (
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">
                        pass issued
                      </span>
                    )}
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${getCheckStatusClasses(visitor.checkStatus)}`}>
                      {getCheckStatusLabel(visitor.checkStatus)}
                    </span>
                  </div>
                  {visitor.isScheduled && (
                    <p className="mt-3 text-sm text-gray-500">Date: {visitor.visitDate} | Time: {visitor.visitTime}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    Entry Status: <span className="font-medium text-gray-700">{getCheckStatusLabel(visitor.checkStatus)}</span>
                  </p>
                  {visitor.checkStatus === "checked-in" && visitor.lastCheckIn && (
                    <p className="mt-2 text-sm font-medium text-green-700">
                      Checked in at {formatDateTime(visitor.lastCheckIn)}
                    </p>
                  )}
                  {visitor.checkStatus === "checked-out" && (
                    <div className="mt-2 space-y-1 text-sm">
                      {visitor.lastCheckIn && (
                        <p className="text-gray-500">Checked in: {formatDateTime(visitor.lastCheckIn)}</p>
                      )}
                      {visitor.lastCheckOut && (
                        <p className="font-medium text-orange-700">Checked out: {formatDateTime(visitor.lastCheckOut)}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:items-end">
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  {visitor.status === "pending" && canManageVisitors && (
                    <>
                      <button onClick={() => approveVisitor(visitor._id)} className="rounded-lg bg-green-500 px-4 py-2 text-sm text-white transition hover:bg-green-600">Approve</button>
                      <button onClick={() => rejectVisitor(visitor._id)} className="rounded-lg bg-red-500 px-4 py-2 text-sm text-white transition hover:bg-red-600">Reject</button>
                    </>
                  )}

                  {visitor.status === "approved" && !visitor.isScheduled && canManageVisitors && (
                    <button onClick={() => openScheduleBox(visitor._id)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition hover:bg-blue-700">Schedule Appointment</button>
                  )}

                  {visitor.status === "approved" && visitor.isScheduled && !visitor.passIssued && canIssuePass && (
                    <button
                      onClick={() => issuePass(visitor._id)}
                      disabled={issuingPassId === visitor._id}
                      className="rounded-lg bg-yellow-500 px-4 py-2 text-sm text-white transition hover:bg-yellow-600 disabled:cursor-not-allowed disabled:bg-yellow-300"
                    >
                      {issuingPassId === visitor._id ? "Issuing..." : "Issue Pass"}
                    </button>
                  )}
                </div>

                {scheduleVisitorId === visitor._id && canManageVisitors && (
                  <div className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 lg:w-72">
                    <div className="flex flex-col gap-2">
                      <input type="date" className="rounded-lg border border-gray-200 bg-white p-2 outline-none focus:border-blue-400" value={visitDate} onChange={e => setVisitDate(e.target.value)} />
                      <input type="time" className="rounded-lg border border-gray-200 bg-white p-2 outline-none focus:border-blue-400" value={visitTime} onChange={e => setVisitTime(e.target.value)} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => scheduleAppointment(visitor._id)} className="mt-3 rounded-lg bg-green-600 px-3 py-2 text-sm text-white transition hover:bg-green-700">Save</button>
                      <button onClick={closeScheduleBox} className="mt-3 rounded-lg bg-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-300">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {role === "admin" && (
          <div className="mt-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Staff Management</h2>
                <p className="mt-1 text-sm text-gray-500">Delete staff accounts one by one when needed.</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {usersLoading && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                  Loading staff users...
                </div>
              )}

              {!usersLoading && users.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                  No staff users found.
                </div>
              )}

              {!usersLoading && users.map((user) => {
                const isSelf = user._id === currentUserId;

                return (
                  <div key={user._id} className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3">
                      <div>
                        <p className="font-semibold text-gray-800">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-700">
                            {user.role}
                          </span>
                          {isSelf && (
                            <span className="rounded-full bg-blue-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">
                              Current Account
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => deleteUser(user._id)}
                      disabled={isSelf || isDeletingUsers}
                      className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isDeletingUsers ? "Deleting..." : "Delete User"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
